import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

/**
 * CountryContext — fournit la juridiction active (BE/FR/LU/CH-FR) à toute l'app.
 *
 * Logique de résolution :
 *  1. Si l'utilisateur est connecté → utilise `user.country`
 *  2. Sinon, lit le pays sauvegardé dans localStorage (`country_preference`)
 *  3. Sinon, appelle GET /api/geo/detect (auto-détection IP + Accept-Language)
 *
 * Utilisation :
 *   const { country, countries, setCountry } = useCountry();
 *   <p>Police : {country.police}</p>
 */

const CountryContext = createContext(null);

const STORAGE_KEY = "country_preference";

// Fallback minimal pour render initial avant le fetch.
const FALLBACK_COUNTRY = {
  id: "be",
  name: "Belgique",
  flag: "🇧🇪",
  police: "101",
  emergency: "112",
  police_complaint_body: "Comité P",
  antiscam_org: "Safeonweb",
  antiscam_phone: "0800/120.33",
  is_beta: false,
  has_regions: true,
};

export const CountryProvider = ({ children }) => {
  const { user } = useAuth();
  const [countries, setCountries] = useState([]);
  const [countryId, setCountryId] = useState(null);
  const [loading, setLoading] = useState(true);

  // Charge la liste des pays publics + détecte
  useEffect(() => {
    const init = async () => {
      try {
        const [listRes, detectRes] = await Promise.all([
          api.get("/countries"),
          api.get("/geo/detect"),
        ]);
        setCountries(listRes.data || []);
        const stored = localStorage.getItem(STORAGE_KEY);
        const detected = detectRes.data?.detected || "be";
        const initial = (user?.country) || stored || detected;
        setCountryId(initial);
      } catch (e) {
        console.warn("CountryContext init failed:", e);
        setCountries([FALLBACK_COUNTRY]);
        setCountryId(user?.country || localStorage.getItem(STORAGE_KEY) || "be");
      } finally {
        setLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Synchroniser quand l'utilisateur se connecte/déconnecte
  useEffect(() => {
    if (user?.country && user.country !== countryId) {
      setCountryId(user.country);
    }
  }, [user, countryId]);

  const setCountry = useCallback(async (newId) => {
    setCountryId(newId);
    localStorage.setItem(STORAGE_KEY, newId);
    // Si connecté, persister côté serveur
    if (user) {
      try {
        await api.patch("/auth/me", { country: newId });
      } catch (e) {
        console.warn("Failed to persist country:", e);
      }
    }
  }, [user]);

  // Pays actif (objet complet)
  const country = countries.find((c) => c.id === countryId) || FALLBACK_COUNTRY;

  return (
    <CountryContext.Provider value={{ country, countries, countryId, setCountry, loading }}>
      {children}
    </CountryContext.Provider>
  );
};

export const useCountry = () => {
  const ctx = useContext(CountryContext);
  if (!ctx) {
    // Permet l'utilisation hors provider (renvoie le fallback)
    return { country: FALLBACK_COUNTRY, countries: [], countryId: "be", setCountry: () => {}, loading: false };
  }
  return ctx;
};
